import { Circle } from '@mui/icons-material';
import { Box } from '@mui/system';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import type { EventGroupResponse, EventDto, SpecialOfferResponse } from 'api/generated';
import { getApiEventGroupsByEventGroupIdEventsCalendarOptions, getApiEventGroupsByEventGroupIdOptions } from 'api/generated/@tanstack/react-query.gen';
import { Calendar } from 'components/common/calendar/calendar';
import { CalendarNavigation } from 'components/common/calendar/calendar-navigation';
import { EventFilterButton } from 'components/events-list/event-filter-button';
import { useCalendar } from 'lib/hooks/use-calendar';
export default function EventsCalendarPaginatedContainer({
  eventGroupId,
  specialOffersOnly,
  setSpecialOffersOnly,
  appliedSpecialOffer,
  setAvailableSpecialOffers
}: {
  eventGroupId: number;
  specialOffersOnly: boolean;
  setSpecialOffersOnly: (value: boolean) => void;
  appliedSpecialOffer: {
    specialOfferId: number;
    specialOfferPassword?: string;
  } | null;
  setAvailableSpecialOffers: (value: SpecialOfferResponse[]) => void;
}) {
  const calendar = useCalendar({
    weekStartsOn: 1 // TODO eh locale based?
  });

  // Data fetching
  const eventGroupQuery = useQuery({
    ...getApiEventGroupsByEventGroupIdOptions({
      path: {
        eventGroupId
      },
      query: {
        SpecialOfferId: specialOffersOnly && appliedSpecialOffer ? appliedSpecialOffer.specialOfferId : undefined,
        SpecialOfferPassword: specialOffersOnly && appliedSpecialOffer ? appliedSpecialOffer.specialOfferPassword : undefined
      }
    }),
    enabled: !!eventGroupId
  });
  const [eventsUseParams, setEventsUseParams] = useState<boolean>(false);
  const eventsQuery = useQuery({
    ...getApiEventGroupsByEventGroupIdEventsCalendarOptions({
      path: {
        eventGroupId
      },
      query: {
        Year: eventsUseParams ? calendar.viewing.getFullYear() : undefined,
        Month: eventsUseParams ? calendar.viewing.getMonth() + 1 : undefined,
        // Months are 1-based in the API
        SpecialOfferId: specialOffersOnly && appliedSpecialOffer ? appliedSpecialOffer.specialOfferId : undefined,
        SpecialOfferPassword: specialOffersOnly && appliedSpecialOffer ? appliedSpecialOffer.specialOfferPassword : undefined
      }
    }),
    enabled: !!eventGroupId
  });
  const availableSpecialOffers = useMemo(() => {
    if (!eventsQuery.data) return [];
    const offers = eventsQuery.data.specialOffers as SpecialOfferResponse[];
    const uniqueOffers = new Map();
    offers.forEach(o => {
      if (o.specialOfferId && !uniqueOffers.has(o.specialOfferId) && o.eventGroup?.eventGroupId === eventGroupId && o.specialOfferId !== appliedSpecialOffer?.specialOfferId) {
        uniqueOffers.set(o.specialOfferId, o);
      }
    });
    return Array.from(uniqueOffers.values()) as SpecialOfferResponse[];
  }, [eventsQuery.data, eventGroupId, appliedSpecialOffer?.specialOfferId]);
  useEffect(() => {
    setAvailableSpecialOffers(availableSpecialOffers);
  }, [availableSpecialOffers, setAvailableSpecialOffers]);

  // Signature of the filter the calendar is currently focused for. The
  // event-group query key derives from the same values, so this stays in sync
  // with the data that query returns within a single render.
  const filterSignature = `${specialOffersOnly}:${appliedSpecialOffer?.specialOfferId ?? 'none'}`;
  const [focusedFilter, setFocusedFilter] = useState<string | null>(null);
  useEffect(() => {
    // Focus the calendar on the first available month for the current filter,
    // re-focusing whenever the filter changes. availableMonths comes from the
    // event-group query, which is offer-aware; the calendar events query
    // returns no events without an explicit month, so it cannot be used to
    // derive the month when a special offer is applied.
    //
    // Wait until the offer-aware response has finished loading before reading
    // availableMonths, so we never focus using data left over from the
    // previous filter.
    if (focusedFilter === filterSignature || eventGroupQuery.isFetching) return;
    const availableMonths = (eventGroupQuery.data as EventGroupResponse)?.availableMonths;
    if (availableMonths && availableMonths.length > 0) {
      const firstMonth = availableMonths[0];
      if (firstMonth?.year && firstMonth?.month) {
        calendar.setViewing(new Date(firstMonth.year, firstMonth.month - 1));
      }
      setEventsUseParams(true);
      setFocusedFilter(filterSignature);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterSignature, focusedFilter, eventGroupQuery.data, eventGroupQuery.isFetching]);

  // Filters
  const [year, setYear] = useState<number>(calendar.viewing.getFullYear());
  useEffect(() => {
    setYear(calendar.viewing.getFullYear());
  }, [calendar.viewing]);
  return <>
      <Box sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto 1fr',
      alignItems: 'center',
      gap: 1,
      mb: {
        xs: 2,
        md: 5
      }
    }}>
        <Box>
          {appliedSpecialOffer && <EventFilterButton clearable={specialOffersOnly} active={false} label={<FormattedMessage id="offer.specialOffer" defaultMessage={[{
          "type": 0,
          "value": "Special offer"
        }]} />} action={() => setSpecialOffersOnly(!specialOffersOnly)} onClear={() => setSpecialOffersOnly(false)} customEndIcon={<Circle sx={{
          width: 16,
          height: 16,
          color: 'primary.main'
        }} />} />}
        </Box>
        <CalendarNavigation calendar={calendar} year={year} setYear={setYear} availableMonths={(eventGroupQuery.data as EventGroupResponse)?.availableMonths || []} />
        <Box />
      </Box>
      <Calendar calendarInstance={calendar} events={eventsQuery.data?.events?.map((event: EventDto) => event) ?? []} eventGroupId={eventGroupId} loading={eventsQuery.isLoading} firstMonth={eventGroupQuery.data?.availableMonths?.[0]} />
    </>;
}